/**
 * The flavour database's model: ten families, 140 notes, and an ingredient's
 * score on each.
 *
 * This is reference data, like the colour table it replaces. The flavour database
 * scores every ingredient 0–10 on 140 named notes (Citric, Oaky, Peaty…),
 * grouped into ten families, plus a score per family and five tastes. The
 * dataset itself lives in the table under `FLAVOUR#` (keys.ts) and is loaded
 * from a mirror of its API (tools/flavour-mirror.mjs, then
 * `npm run flavour:load`); what is here is the vocabulary needed to read it.
 *
 * ## Order is data
 *
 * A profile is stored as plain arrays, so the position of each note and
 * family is what names it. `FLAVOUR_FAMILIES` and their `notes` are in
 * the flavour database's own order: flattening them gives index 0 (Sugary) to 139
 * (Milky), exactly the order of their `rawVector`. The family scores arrive
 * at indexes 140–149 in a different order (Maillard first); `sourceIndex`
 * records where each family's score is found so the profile can store them
 * in this module's order instead. Do not reorder anything here without
 * reloading the dataset.
 *
 * ## Names
 *
 * Each note has an internal `name` (Celery, Cinchona, Poivre) — the key the
 * data uses and what the chat reasons with — and a display `label` (Celery-
 * like, Quinine, Peppery) that the wheel prints. Family keys are the names a
 * bartender reads: the flavour database calls the second family "Acidic" and shows it
 * as SHARP, so here it is `Sharp`, and its "Nectarous" group score is `Sweet`.
 */
export const FLAVOUR_FAMILY_KEYS = [
    "Sweet",
    "Sharp",
    "Floral",
    "Herbal",
    "Vegetal",
    "Spice",
    "Woody",
    "Earthy",
    "Maillard",
    "Carnal",
];
export const FLAVOUR_FAMILIES = [
    {
        key: "Sweet",
        colour: "#59A0C4",
        sourceKey: "Sweet",
        sourceIndex: 149,
        notes: ["Sugary", "Honeyed", "Caramel", "Molasses", "Maple", "Balsam", "Raisin", "Peach", "Pear", "Raspberry", "Blackberry", "Banana", "Melon", "Mango"],
    },
    {
        key: "Sharp",
        colour: "#8489C2",
        sourceKey: "Acidic",
        sourceIndex: 148,
        notes: ["Lychee", "Pineapple", "Passionfruit", "Grapefruit", "Gooseberry", "Orange", "Citric", "Malic", "Lactic", "Acetic", "Butyric", "Proteolytic", "Brettanomyces", "Oxidized"],
    },
    {
        key: "Floral",
        colour: "#9577AF",
        sourceKey: "Floral",
        sourceIndex: 146,
        notes: ["Cherry", "Plum", "Apricot", "Ficus", "Neroli", "Bergamot", "Jasmine", "Hibiscus", "Elderflower", "Safranal", "Rose", "Lavender", "Violet", "Chamomile"],
    },
    {
        key: "Herbal",
        colour: "#BD768F",
        sourceKey: "Herbal",
        sourceIndex: 147,
        notes: ["Tannic", "Tobacco", "Coffee", "Cocoa", "Vanillic", "Cinchona", "Eucalyptol", "Hoppy", "Menthol", "Basil", "Thyme", "Sage", "Bay leaf", "Rosemary"],
    },
    {
        key: "Vegetal",
        colour: "#DE7170",
        sourceKey: "Vegetal",
        sourceIndex: 145,
        notes: ["Seaweed", "Olivey", "Tomatoey", "Celery", "Cucumber", "Squash", "Asparagus", "Grassy", "Chlorophyll", "Spinach", "Brassica", "Sulfurous", "Bean", "Pea"],
    },
    {
        key: "Spice",
        colour: "#DF8353",
        sourceKey: "Spice",
        sourceIndex: 144,
        notes: ["Nutmeg", "Fennel", "Cinnamon", "Sotolon", "Coriander seed", "Clove", "Camphor", "Allspice", "Ginger", "Allicin", "Mustard", "Poivre", "Capsicum", "Capsaicin"],
    },
    {
        key: "Woody",
        colour: "#DF903C",
        sourceKey: "Woody",
        sourceIndex: 143,
        notes: ["Astringent", "Coconut", "Almond", "Hazelnut", "Walnut", "Chestnut", "Seedy", "Sesame", "Gentian", "Pine", "Oaky", "Cedar", "Hickory"],
    },
    {
        key: "Earthy",
        colour: "#D7AB6C",
        sourceKey: "Earthy",
        sourceIndex: 142,
        notes: ["Saline", "Alcohol", "Iron", "Copper", "Flint", "Graphite", "Limestone", "Petrichor", "Peaty", "Mossy", "Soil", "Fungus", "Porcini", "Mouldy", "Koji"],
    },
    {
        key: "Maillard",
        colour: "#89AD8B",
        sourceKey: "Maillard",
        sourceIndex: 140,
        notes: ["Yeasty", "Potato", "Onion", "Parsnip", "Corn", "Rice", "Wheat", "Maltol", "Hay", "Toasted", "Charred", "Ash", "Smoky", "Leather"],
    },
    {
        key: "Carnal",
        colour: "#6FA5A9",
        sourceKey: "Carnal",
        sourceIndex: 141,
        notes: ["Glutamic", "Fishy", "Oyster", "Oceanic", "Poultry", "Porcine", "Bovine", "Ovine", "Gamey", "Musky", "Adipose", "Oleic", "Buttery", "Milky"],
    },
];
/**
 * The label the wheel prints for each note, by index. Taken from the facet
 * objects in the flavour database's own responses, so they are its wording.
 */
const NOTE_LABELS = [
    "Sugary", "Honeyed", "Caramel", "Molasses", "Maple", "Balsam", "Raisiny", "Peachy", "Pear-like", "Red Berry",
    "Blackberry", "Banana-like", "Melony", "Mango-like", "Lychee-like", "Pineappley", "Passionfruity", "Grapefruity", "Gooseberry", "Orangey",
    "Citric", "Malic", "Lactic", "Vinegary", "Butyric", "Proteolytic", "Barnyard", "Rancio", "Cherry-like", "Plummy",
    "Apricoty", "Figgy", "Neroli", "Bergamot", "Jasmine", "Hibiscine", "Elder", "Safranal", "Rosy", "Lavandular",
    "Violic", "Chamomillic", "Tannic", "Tobacco", "Coffee", "Cocoa", "Vanillic", "Quinine", "Eucalyptol", "Hoppy",
    "Menthol", "Basilicum", "Thyme", "Sagey", "Laurelled", "Rosmarinic", "Algal", "Olivey", "Tomatoey", "Celery-like",
    "Cucumber", "Squashy", "Asparagous", "Grassy", "Chlorophyll", "Spinachy", "Brassical", "Sulfurous", "Beany", "Pea-ish",
    "Nutmeggy", "Fennelly", "Cinnamonic", "Sotolon", "Coriandric", "Eugenolic", "Camphor", "Pimento", "Gingery", "Garlicy",
    "Mustardy", "Peppery", "Capsicum", "Spicy heat", "Astringent", "Coconuty", "Almondy", "Hazel", "Walnutty", "Chestnutty",
    "Flaxseedy", "Sesame", "Gentian", "Piney", "Oaky", "Cedar", "Hickory", "Briny", "Alcohol", "Irony",
    "Cupric", "Flint", "Graphite", "Chalky", "Petrichor", "Peaty", "Mossy", "Loamy", "Chanterelle", "Porcini",
    "Mouldy", "Koji", "Yeasty", "Potatoey", "Oniony", "Parsnipped", "Maize", "Ricey", "Wheaty", "Malty",
    "Hay", "Toasted", "Charred", "Ashy", "Smoky", "Leather", "Glutamic", "Sardiney", "Oystery", "Oceanic",
    "Chickeny", "Porky", "Beefy", "Lamby", "Gamey", "Musky", "Fatty", "Oleic", "Buttery", "Milky",
];
/** All 140 notes, in vector order. */
export const FLAVOUR_NOTES = FLAVOUR_FAMILIES.flatMap((family) => family.notes).map((name, index) => ({
    index,
    name,
    label: NOTE_LABELS[index] ?? name,
    family: FLAVOUR_FAMILIES.find((f) => f.notes.includes(name)).key,
}));
export const FLAVOUR_NOTE_COUNT = 140;
/** The five tastes at indexes 150–154 of the flavour database's vector, in that order. */
export const FLAVOUR_TASTES = ["Sweet", "Salty", "Sour", "Umami", "Bitter"];
/** Where the tastes start in the flavour database's vector. */
const TASTE_SOURCE_INDEX = 150;
/** The length of the flavour database's vector: 140 notes, 10 families, 5 tastes. */
export const FLAVOUR_SOURCE_LENGTH = 155;
/** Grey for anything with no family: water, ice, a line with no flavour. */
export const FLAVOUR_NEUTRAL_COLOUR = "#B8B5AF";
const FAMILY_BY_KEY = new Map(FLAVOUR_FAMILIES.map((f) => [f.key, f]));
const NOTE_BY_NAME = new Map(FLAVOUR_NOTES.map((n) => [n.name.toLowerCase(), n]));
export function isFlavourFamily(value) {
    return typeof value === "string" && FAMILY_BY_KEY.has(value);
}
export function flavourFamily(key) {
    return FAMILY_BY_KEY.get(key);
}
/**
 * The twelve families BarOS used before the flavour database (the design prototype's),
 * and where each went. Ingredients and guests saved before the switch still
 * carry these words; `toFlavourFamily` reads them as the new ones, so nothing
 * stored needs migrating. Dairy's butter and milk are in Carnal; Marine's
 * brine is Earthy's Saline; Sulphurous is a Vegetal note.
 */
export const LEGACY_FAMILIES = {
    Citrus: "Sharp",
    Fruity: "Sweet",
    Floral: "Floral",
    Herbaceous: "Herbal",
    Vegetal: "Vegetal",
    Spice: "Spice",
    Woody: "Woody",
    Roasted: "Maillard",
    Sweet: "Sweet",
    Dairy: "Carnal",
    Marine: "Earthy",
    Sulphurous: "Vegetal",
};
/** A family key from either vocabulary, case-insensitively; null for anything else. */
export function toFlavourFamily(name) {
    if (!name)
        return null;
    const trimmed = name.trim().toLowerCase();
    const current = FLAVOUR_FAMILY_KEYS.find((k) => k.toLowerCase() === trimmed);
    if (current)
        return current;
    const legacy = Object.keys(LEGACY_FAMILIES).find((k) => k.toLowerCase() === trimmed);
    return legacy ? LEGACY_FAMILIES[legacy] : null;
}
/**
 * What a guest reads for each family. Maillard and Carnal are precise words
 * for a bartender and baffling ones on a phone at the door, so the guest
 * page says Toasty and Rich (Rob, 2026-10-04). Stored as the keys either way.
 */
export const FLAVOUR_GUEST_LABELS = {
    Sweet: "Sweet",
    Sharp: "Sharp",
    Floral: "Floral",
    Herbal: "Herbal",
    Vegetal: "Green",
    Spice: "Spiced",
    Woody: "Woody",
    Earthy: "Earthy",
    Maillard: "Toasty",
    Carnal: "Rich",
};
/** The colour for a family, from either vocabulary, or the neutral grey for none. */
export function flavourColour(key) {
    const family = toFlavourFamily(key);
    return (family && FAMILY_BY_KEY.get(family)?.colour) || FLAVOUR_NEUTRAL_COLOUR;
}
/** A note by internal name, case-insensitively, or undefined. */
export function flavourNote(name) {
    return NOTE_BY_NAME.get(name.trim().toLowerCase());
}
/** A score clamped to the 0–10 scale; anything not a finite number is 0. */
export function clampScore(value) {
    return typeof value === "number" && Number.isFinite(value) ? Math.min(10, Math.max(0, value)) : 0;
}
/**
 * Two decimals is finer than the flavour database publishes (one) and absorbs float
 * noise like 0.30000000000000004 so stored items stay small and comparable.
 */
function tidy(value) {
    return Math.round(clampScore(value) * 100) / 100;
}
/**
 * A profile from the flavour database's 155-number `rawVector`. Throws on a vector of
 * the wrong length, because positions are names and a short one would
 * silently shift every note after the gap.
 */
export function flavourProfileFromVector(name, vector) {
    if (vector.length !== FLAVOUR_SOURCE_LENGTH) {
        throw new RangeError(`${name}: expected a ${FLAVOUR_SOURCE_LENGTH}-number vector, got ${vector.length}`);
    }
    return {
        name,
        notes: vector.slice(0, FLAVOUR_NOTE_COUNT).map(tidy),
        families: FLAVOUR_FAMILIES.map((f) => tidy(vector[f.sourceIndex])),
        tastes: FLAVOUR_TASTES.map((_, i) => tidy(vector[TASTE_SOURCE_INDEX + i])),
    };
}
/**
 * A stored item read back as a profile, or null when it is not one. Lenient
 * about values (clamped) and strict about shape (lengths), for the reason
 * `flavourProfileFromVector` is.
 */
export function parseFlavourProfile(raw) {
    if (typeof raw !== "object" || raw === null)
        return null;
    const r = raw;
    const { name, notes, families, tastes } = r;
    if (typeof name !== "string" || !Array.isArray(notes) || !Array.isArray(families))
        return null;
    if (notes.length !== FLAVOUR_NOTE_COUNT || families.length !== FLAVOUR_FAMILIES.length)
        return null;
    return {
        name,
        notes: notes.map(clampScore),
        families: families.map(clampScore),
        tastes: Array.isArray(tastes) && tastes.length === FLAVOUR_TASTES.length ? tastes.map(clampScore) : FLAVOUR_TASTES.map(() => 0),
    };
}
/**
 * The family a profile leans on most, by its family scores; ties go to the
 * earlier family. Null when every family scores zero — water, say.
 */
export function dominantFlavourFamily(profile) {
    let best = -1;
    let score = 0;
    profile.families.forEach((value, i) => {
        if (value > score) {
            score = value;
            best = i;
        }
    });
    return best < 0 ? null : FLAVOUR_FAMILIES[best].key;
}
/**
 * The `n` strongest notes, strongest first, above `min` (exclusive). Ties keep
 * vector order, so the same profile always lists the same way.
 */
export function topFlavourNotes(profile, n, min = 0) {
    return FLAVOUR_NOTES.map((note) => ({ ...note, value: clampScore(profile.notes[note.index]) }))
        .filter((note) => note.value > min)
        .sort((a, b) => b.value - a.value || a.index - b.index)
        .slice(0, Math.max(0, n));
}
/**
 * Notes with an org's own scores laid over the flavour database's, keyed by internal
 * note name. An override replaces the score outright, before any other maths,
 * which is how the reference wheel's "Edit flavour profile" behaves.
 */
export function withFlavourOverrides(profile, overrides) {
    if (!overrides || Object.keys(overrides).length === 0)
        return profile;
    const notes = [...profile.notes];
    for (const [name, value] of Object.entries(overrides)) {
        const note = flavourNote(name);
        if (note)
            notes[note.index] = clampScore(value);
    }
    return { ...profile, notes };
}
/** Lowercased and trimmed: how a flavour-database name is keyed, since it matches case-insensitively. */
export function flavourNameKey(name) {
    return name.trim().replace(/\s+/g, " ").toLowerCase();
}
//# sourceMappingURL=flavour.js.map